import { useEffect, useMemo, useRef, useState, useContext } from "react";
import { ExplorerIcon, FavoritesIcon, HomeIcon, RecentIcon, SearchIcon, TrashIcon, BulletListIcon } from "./Icon"
import { cn } from "../utils/string";
import logo from "../assets/icons/igarment_logo.svg";
import CreateSpaceModal from './CreateSpace/CreateSpaceModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faEllipsisH, faUserGroup, faPenToSquare, faTrash, faUser, faBook } from '@fortawesome/free-solid-svg-icons';
import ManageAccessModal from './ManageAccess/ManageAccessModal';
import RenameSpaceModal from "./RenameSpaceModal";
import DeleteModal from "./DeleteModal";
import service from "../utils/request";
import message from "../utils/message";
import { SpaceContext } from "../SpaceContext";

// Helper function with object lookup for space class and id
const spaceIcons = {
    user: (props) => <HomeIcon {...props} />,
    search: (props) => <SearchIcon {...props} />,
    recent: (props) => <RecentIcon {...props} />,
    trash: (props) => <TrashIcon {...props} />,
    favour: (props) => <FavoritesIcon {...props} />,
    explorer: (props) => <ExplorerIcon {...props} />,
    create: (props) => <BulletListIcon {...props} color="gray-100" />,
    shared: (props) => <BulletListIcon {...props} />,
    share: (props) => <BulletListIcon {...props} />,
    library: ({ color, ...props }) => <FontAwesomeIcon icon={faBook} className="w-4 h-4" color={color || "#5B616D"} {...props} />,
    orphan: ({ color, ...props }) => <FontAwesomeIcon icon={faUser} className="w-4.5 h-4.5" color={color || "#5B616D"} {...props} />,
};

function getSpaceIcon(spaceClass, spaceId, props) {
    // Check if spaceClass is 'create'

    let Icon = spaceIcons[spaceClass];
    if (!Icon) {
        Icon = spaceIcons[spaceId];
    }
    if (!Icon) {
        // Fallback to a default icon instead of throwing error
        console.warn(`No icon found with spaceClass: ${spaceClass} and spaceId: ${spaceId}`);
        return <BulletListIcon {...props} color="#6B7280" />;
    }
    return <Icon {...props} />;
}

export function SpaceIcon({ space, isSelected }) {
    return (
        <div className="flex items-center justify-center w-5 h-5">
            {isSelected ? getSpaceIcon(space.class, space.id, { color: "#0A0C11" }) :
                getSpaceIcon(space.class, space.id)}
        </div>
    );
}

function Space({ space, isSelected, onManageAccess }) {
    return (
        <div
            className={cn(
                space.id === 'create'
                    ? "flex items-center border border-gray-300 justify-between p-2.5  rounded hover:bg-gray-100 cursor-pointer"
                    : "flex transition  items-center p-2.5 text-gray-900 text-sm font-semibold hover:bg-white cursor-pointer group relative justify-between",
                isSelected && space.id !== 'create' && "bg-white"
            )}
        >
            {
                space.id === 'create' ? (
                    <div className="flex items-center grow justify-between">
                        <span className="text-sm font-semibold">{space.caption}</span>
                        <FontAwesomeIcon
                            icon={faPlus}
                            className="w-3 h-3 text-gray-600"
                        />
                    </div>
                ) : (
                    <div className="flex items-center">
                        <SpaceIcon space={space} isSelected={isSelected} />
                        <span className="ms-3">{space.caption}</span>
                    </div>
                )}
            {space.class === 'share' && (
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        onManageAccess(space);
                    }}
                    className="p-1 transition-all rounded opacity-0 group-hover:opacity-100 hover:bg-gray-300"
                >
                    <svg className="w-4 h-4 text-gray-600" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 4a4 4 0 100 8 4 4 0 000-8zM6 20v-2a4 4 0 014-4h4a4 4 0 014 4v2"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round" />
                    </svg>
                </button>
            )}
        </div>
    );
}

const FilteredSpace = ({ isSelected, space, onOptionsClick, showOptions, onManageAccess, onRename, onDelete, optionsRef }) => {

    const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 });
    const buttonRef = useRef(null);

    const handleOptionsClick = (e) => {
        e.stopPropagation();
        const button = buttonRef.current;
        if (button) {
            const rect = button.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;
            const popupHeight = 120;
            const horizontalOffset = 10;
            const rowHeight = rect.height * 1.8;

            const left = rect.right + horizontalOffset;
            const top = rect.top - rowHeight;

            const finalTop = spaceBelow < popupHeight ? rect.bottom - popupHeight : top;

            setPopupPosition({ top: finalTop, left });
            onOptionsClick(space, e);
        }
    };

    return (
        <>
            <div className={`flex items-center justify-between p-2 text-base font-normal hover:bg-gray-200 cursor-pointer group
        ${isSelected ? 'bg-gray-200' : ''}`}>
                <div className="flex items-center">
                    <SpaceIcon space={space} isSelected={isSelected} />
                    <span className="ml-3">{space.caption}</span>
                </div>
                {space.class === 'share' && (
                    <div>
                        <button
                            ref={buttonRef}
                            onClick={handleOptionsClick}
                            className="px-1 transition-all rounded opacity-0 group-hover:opacity-100 hover:bg-gray-300"
                        >
                            <FontAwesomeIcon icon={faEllipsisH} className="w-4 h-4 text-gray-600" />
                        </button>
                        {showOptions === space.id && (
                            <div
                                ref={optionsRef}
                                style={{
                                    position: 'fixed',
                                    top: `${popupPosition.top}px`,
                                    left: `${popupPosition.left}px`,
                                }}
                                className="z-50 bg-white rounded-lg shadow-lg border border-gray-200 py-1 w-48"
                            >
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onManageAccess(space);
                                    }}
                                    className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center gap-2"
                                >
                                    <FontAwesomeIcon icon={faUserGroup} className="w-4 h-4" />
                                    Manage Access
                                </button>
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onRename(space);
                                    }}
                                    className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center gap-2"
                                >
                                    <FontAwesomeIcon icon={faPenToSquare} className="w-4 h-4" />
                                    Rename
                                </button>
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onDelete(space);
                                    }}
                                    className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-gray-100 flex items-center gap-2"
                                >
                                    <FontAwesomeIcon icon={faTrash} className="w-4 h-4" />
                                    Delete
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </>
    )
}

const Sidebar = ({ spaces, switchSpace, setSpaces }) => {
    const { getFilteredSpaces } = useContext(SpaceContext);

    const [currentSpaceId, setCurrentSpaceId] = useState("");
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isManageAccessOpen, setIsManageAccessOpen] = useState(false);
    const [selectedSpace, setSelectedSpace] = useState(null);
    const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [showOptionsForSpace, setShowOptionsForSpace] = useState(null);
    const optionsRef = useRef(null);
    const [orphanSpaces, setOrphanSpaces] = useState([]);
    const [searchSpace, setSearchSpace] = useState("");

    useEffect(() => {
        service.get('/filer/get_orphan_spaces')
            .then(res => {
                if (Array.isArray(res)) {
                    setOrphanSpaces(res.map(item => ({
                        id: item.space_id,
                        caption: item.space_name,
                        class: 'orphan'
                    })));
                }
            })
            .catch(err => {
                console.error("Error fetching orphan spaces:", err);
            });
    }, []);

    const shareSpace = useMemo(() => {
        let result = [...spaces];

        result = result.filter(space =>
            space.class === 'share' || space.caption === 'General' || space.caption === 'Legacy'
        );

        result.sort((a, b) => a.caption.localeCompare(b.caption));

        return [...result, ...orphanSpaces];
    }, [spaces, orphanSpaces])

    const filteredShareSpace = useMemo(() => {
        if (!searchSpace.trim()) return shareSpace;
        return shareSpace.filter(space =>
            space.caption.toLowerCase().includes(searchSpace.toLowerCase())
        );
    }, [shareSpace, searchSpace]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (optionsRef.current && !optionsRef.current.contains(event.target)) {
                setShowOptionsForSpace(null);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        const sessionPath = sessionStorage.getItem("path");
        if (sessionPath) {
            const pathParse = JSON.parse(sessionPath);
            setCurrentSpaceId(pathParse[0]?.id);
            return;
        }
        if (spaces) {
            setCurrentSpaceId(spaces[0]?.id);
        }
    }, [spaces]);

    const handleSpaceChange = (space) => {
        if (space.caption === 'New Space') {
            setIsCreateModalOpen(true);
        } else {
            setCurrentSpaceId(space.id);
            switchSpace(space);
        }
    };

    const handleCreateSpace = async (spaceName) => {
        try {
            const newSpace = await service.post('/filer/create_space', {
                space_name: spaceName,
                space_class: 'share'
            })

            const spaceItems = await service.get(`/filer/list/${newSpace.id}`);
            const filteredSpaces = await getFilteredSpaces();

            sessionStorage.setItem("spaces", JSON.stringify(filteredSpaces));
            setSpaces(filteredSpaces);

            setCurrentSpaceId(newSpace.id);
            switchSpace({
                id: newSpace.id,
                caption: newSpace.name,
                class: 'share',
                shared: true,
                items: spaceItems
            });

            setIsCreateModalOpen(false);
            message('success', 'Space created successfully');
        } catch (error) {
            console.error('Error:', error);
            message('error', error);
        }
    };

    const handleManageAccess = (space) => {
        setSelectedSpace(space);
        setIsManageAccessOpen(true);
        setShowOptionsForSpace(null);
    };

    const handleOptionsClick = (space, e) => {
        e.stopPropagation();
        setSelectedSpace(space);
        setShowOptionsForSpace(showOptionsForSpace === space.id ? null : space.id);
    };

    const handleRenameClick = (space) => {
        setSelectedSpace(space);
        setIsRenameModalOpen(true);
        setShowOptionsForSpace(null);
    };

    const handleDeleteClick = (space) => {
        setSelectedSpace(space);
        setIsDeleteModalOpen(true);
        setShowOptionsForSpace(null);
    };

    const handleRenameSpace = async (newName) => {
        try {
            await service.post('/filer/rename_space', {
                space_id: selectedSpace.id,
                new_name: newName
            })

            const filteredSpaces = await getFilteredSpaces();
            sessionStorage.setItem("spaces", JSON.stringify(filteredSpaces));
            setSpaces(filteredSpaces);

            message('success', 'Space renamed successfully');
            setIsRenameModalOpen(false);
        } catch (error) {
            message('error', error);
            console.error(error);
        }
    };

    const handleDeleteSpace = async () => {
        try {
            await service.post('/filer/delete_space', {
                space_id: selectedSpace.id
            });

            const filteredSpaces = await getFilteredSpaces();
            sessionStorage.setItem("spaces", JSON.stringify(filteredSpaces));
            setSpaces(filteredSpaces);

            message('success', 'Space deleted successfully');
            setIsDeleteModalOpen(false);
        } catch (error) {
            message('error', error);
            console.error(error);
        }
    };

    return (
        <>
            <aside
                id="default-sidebar"
                className="z-[5] w-56 h-screen fixed transition-transform -translate-x-full sm:translate-x-0 bg-white shadow-lg rounded-r-lg"
                aria-label="Sidebar"
            >
                <div className="h-full px-3 py-2 overflow-y-auto space-y-[18px]">
                    <div className="bg-gray-200 rounded border border-gray-200">
                        <span className="flex text-gray-500 text-[12px] font-bold p-2.5">PERSONAL ITEMS</span>
                        <ul className="font-medium py-2 duration-200">
                            {spaces.filter(space =>
                                (!space.shared || space.id === 'favour' || space.id === 'trash' || space.id === 'recent' || space.id === 'search') &&
                                space.caption !== 'General' &&
                                space.caption !== 'Legacy' &&
                                space.class !== 'share'
                            ).map((space) => (
                                <li key={space.id} onClick={() => handleSpaceChange(space)}>
                                    <Space isSelected={space.id === currentSpaceId} space={space} onManageAccess={handleManageAccess} />
                                </li>
                            ))}
                        </ul>
                    </div>
                    {/* <div onClick={() => handleSpaceChange({id: 'create', class: 'create', caption: 'New Space'})}>
                        <Space
                            isSelected={currentSpaceId === 'create'}
                            space={{id: 'create', class: 'create', caption: 'New Space'}}
                            onManageAccess={handleManageAccess}
                        />
                    </div> */}

                    <div className="rounded border border-gray-300 overflow-hidden flex flex-col bg-white">
                        <div className="px-2 py-1.5 border-b border-gray-200 bg-gray-50 flex items-center gap-1.5">
                            <input
                                type="text"
                                placeholder="Filter spaces..."
                                value={searchSpace}
                                onChange={(e) => setSearchSpace(e.target.value)}
                                className="w-full px-2 py-1 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white text-gray-700"
                            />
                        </div>
                        <ul className="font-medium py-2 max-h-56 overflow-y-auto">
                            {
                                filteredShareSpace.map((space) =>
                                    <li key={space.id} onClick={() => handleSpaceChange(space)}>
                                        <FilteredSpace
                                            isSelected={space.id === currentSpaceId}
                                            space={space}
                                            onOptionsClick={handleOptionsClick}
                                            showOptions={showOptionsForSpace}
                                            onManageAccess={handleManageAccess}
                                            onRename={handleRenameClick}
                                            onDelete={handleDeleteClick}
                                            optionsRef={optionsRef}
                                        />
                                    </li>
                                )
                            }
                        </ul>
                    </div>

                </div>
            </aside>

            {isRenameModalOpen && (
                <div className="fixed inset-0 z-[100]">
                    <RenameSpaceModal
                        isOpen={isRenameModalOpen}
                        onClose={() => setIsRenameModalOpen(false)}
                        onSubmit={handleRenameSpace}
                        spaceName={selectedSpace?.caption}
                    />
                </div>
            )}

            {isDeleteModalOpen && (
                <div className="fixed inset-0 z-[100]">
                    <DeleteModal
                        isOpen={isDeleteModalOpen}
                        onCancel={() => setIsDeleteModalOpen(false)}
                        onConfirm={handleDeleteSpace}
                    />
                </div>
            )}

            {isCreateModalOpen && (
                <CreateSpaceModal
                    isOpen={isCreateModalOpen}
                    onClose={() => setIsCreateModalOpen(false)}
                    onSubmit={handleCreateSpace}
                />
            )}

            {isManageAccessOpen && (
                <ManageAccessModal
                    isOpen={isManageAccessOpen}
                    onClose={() => setIsManageAccessOpen(false)}
                    spaceName={selectedSpace?.caption}
                    spaceId={selectedSpace?.id}
                />
            )}
        </>
    );
};

export default Sidebar;
